import { Capacitor } from "@capacitor/core";

/**
 * Durable store for the mobile video store's per-video media side records
 * (transcript text + chapters), keyed by video id, payload an opaque JSON
 * string (the store owns the schema).
 *
 * Why this exists as a seam: the records used to live in localStorage, whose
 * ~5–10MB WKWebView quota cannot hold a round's worth of transcripts —
 * S-PROCESS-FRONTEND-8A was real devices hitting that ceiling, after which
 * every side write fails and transcripts pile up inline in the main blob (the
 * exact main-thread-serialization problem the side records exist to avoid).
 * On native the records live in per-video files instead (below); the seam
 * also lets tests inject a controllable fake.
 *
 * IndexedDB is deliberately NOT an option here: iOS Lockdown Mode strips it
 * from the WebView entirely (see `offline/hasIndexedDB.ts`), and the primary
 * funder iPad may run Lockdown Mode. Native storage is unaffected — the same
 * reasoning as `offline/capacitorSnapshotIO.ts`.
 */
export interface MediaCacheIO {
  /** The stored payload, or null when no record exists (or it is unreadable —
   * a lost record degrades safely: the download engine refetches). */
  read(id: string): Promise<string | null>;
  /**
   * Whether a record exists, WITHOUT reading it: `true`/`false` when that can
   * be answered cheaply, `null` when it cannot and the caller must fall back
   * to `read`. Optional — an IO that omits it (or answers `null`) just gets
   * the old read-and-catch behavior.
   *
   * Exists because "no record yet" is the common case, not an edge case. A
   * video whose transcript is being cached for the first time has no record
   * by definition, so asking for one is a guaranteed miss — and on native
   * that miss costs a bridge round trip that ends in a constructed NSError.
   * On a fresh install of a 184-video round the device log was wall-to-wall
   * `readFile failed … does not exist`, one per video, running the length of
   * the session. Answering from a directory listing instead is the same trick
   * `capacitorSnapshotIO` already uses for the same reason.
   */
  hasRecord?(id: string): Promise<boolean | null>;
  /** Durably store the payload. MUST throw on failure — callers gate
   * destroying the only other copy on this resolving. */
  write(id: string, data: string): Promise<void>;
  /** Remove the record. Removing a record that does not exist is success
   * (parity with `localStorage.removeItem`); throws only on real failure. */
  remove(id: string): Promise<void>;
  /** True when this IO stores records at the PRE-MIGRATION localStorage keys
   * themselves (the non-native default). The store's legacy-key migration
   * and cleanup must then be inert — "migrating" would read a record from
   * the very location just written and the cleanup would delete it. */
  readonly storesInLegacyLocation?: boolean;
}

// App-private data directory (not user-visible, survives restarts, cleared on
// uninstall). One JSON file per video's media record. Video ids are nanoid
// (`A-Za-z0-9_-`), safe as filenames without encoding.
const MEDIA_CACHE_DIR = "video-media-cache";

function pathFor(id: string): string {
  return `${MEDIA_CACHE_DIR}/${id}.json`;
}

// Runtime values of the plugin's Directory.Data / Encoding.UTF8 enums,
// inlined so this module never STATICALLY binds `@capacitor/filesystem`'s
// named exports. That matters twice over: (a) several test files replace the
// module process-globally with partial factories, and a static named import
// of a member a factory omits is a load-time SyntaxError for whichever suite
// loads later — CI file order dependent (see "Module Mocks in Tests" in the
// webapp AGENTS.md; this bit `videoAvailabilityProbe` via the store's import
// of this file); (b) web sessions never need the plugin loaded at all.
const DATA_DIRECTORY = "DATA";
const UTF8_ENCODING = "utf8";

// The slice of Capacitor's Filesystem plugin the IO uses, typed structurally
// (directory/encoding as plain strings) so neither this module nor test
// fakes need the plugin's enum types. Injectable so the IO's own error
// handling is unit-testable without a process-global module mock.
export interface MediaCacheFilesystem {
  readdir(options: {
    path: string;
    directory: string;
  }): Promise<{ files: ({ name?: string } | string)[] }>;
  readFile(options: {
    path: string;
    directory: string;
    encoding: string;
  }): Promise<{ data: string | Blob }>;
  writeFile(options: {
    path: string;
    directory: string;
    data: string;
    encoding: string;
    recursive: boolean;
  }): Promise<unknown>;
  deleteFile(options: { path: string; directory: string }): Promise<void>;
}

// Loaded lazily on first native use, via a namespace access rather than a
// named import — the runtime lookup goes through CJS interop and tolerates
// any factory shape, unlike static named bindings (comment above).
let realFilesystem: Promise<MediaCacheFilesystem> | null = null;

function loadFilesystem(): Promise<MediaCacheFilesystem> {
  realFilesystem ??= import("@capacitor/filesystem")
    .then((mod) => {
      const plugin = mod.Filesystem as unknown as MediaCacheFilesystem;
      // NEVER resolve a promise WITH the plugin object. Capacitor's plugin
      // objects are proxies that answer every property access with a native
      // method call — including `then`. That makes them look thenable, so the
      // JS runtime "unwraps" them by calling `Filesystem.then(resolve,
      // reject)`, which iOS rejects with "not implemented on ios". Observed on
      // device: this promise rejected at startup and, being memoized, stayed
      // rejected for the whole session — every transcript/chapter read
      // returned null and every write failed, silently, so the records this
      // module exists to move OUT of localStorage kept piling up IN it.
      // Resolving to a plain object of bound methods keeps the proxy off the
      // promise-resolution path.
      const wrapped: MediaCacheFilesystem = {
        readdir: (options) => plugin.readdir(options),
        readFile: (options) => plugin.readFile(options),
        writeFile: (options) => plugin.writeFile(options),
        deleteFile: (options) => plugin.deleteFile(options),
      };

      return wrapped;
    })
    .catch((error: unknown) => {
      // Never memoize a failure: a stuck rejected memo disables the cache for
      // the rest of the session, which is precisely how the bug above turned
      // one startup hiccup into a permanent one.
      realFilesystem = null;
      throw error;
    });

  return realFilesystem;
}

/**
 * Native implementation: per-video JSON files under Directory.Data. Unbounded
 * (no quota class), untouched by Lockdown Mode, and incremental — one video's
 * record per write, never a combined blob.
 */
export function createCapacitorMediaCacheIO(
  fs?: MediaCacheFilesystem,
): MediaCacheIO {
  const resolveFs =
    fs != null ? () => Promise.resolve(fs) : () => loadFilesystem();

  // Ids with a record on disk. Two sources, deliberately kept separate:
  //
  //   `written` — ids THIS IO has written this session. Never replaced, so a
  //   write that lands while the listing is still in flight cannot be lost by
  //   the snapshot that arrives after it. (Folding writes into the listing's
  //   set was a real hole: the readdir snapshot predates the write, installing
  //   a set that permanently omits a record that exists — and "absent" is the
  //   dangerous direction, since it skips the sibling-preserving read.)
  //
  //   `listed` — one directory listing, which is the whole point: a fresh
  //   round asks about every video and every answer is "no", and as a read
  //   that is a bridge round trip per video ending in a native error. Loaded
  //   lazily, so a session that never caches media never pays for it.
  const written = new Set<string>();
  let listed: Set<string> | null = null;
  let listing: Promise<Set<string> | null> | null = null;

  // Capacitor reports a missing path as a plain Error whose message varies by
  // platform, so match on text (same approach as `remove` below and
  // capacitorSnapshotIO). A missing DIRECTORY is the first-launch case and is
  // conclusive: there are no records. Any other failure is not, and must read
  // as "unknown" so callers fall back to reading rather than being told a
  // record is absent when it may not be.
  const isMissing = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error);
    return /does not exist|not found|no such file/i.test(message);
  };

  const loadListing = (): Promise<Set<string> | null> => {
    // Never memoize a failure — the same rule (and the same bug) as
    // loadFilesystem above: a stuck rejected memo would disable the listing
    // for the whole session, silently reinstating the per-video misses.
    listing ??= resolveFs()
      .then((filesystem) =>
        filesystem.readdir({
          path: MEDIA_CACHE_DIR,
          directory: DATA_DIRECTORY,
        }),
      )
      .then((result) => {
        const loaded = new Set<string>();

        for (const entry of result.files) {
          const name = typeof entry === "string" ? entry : entry.name;

          // Store ids, not filenames, so callers never re-derive the path.
          if (name != null && name.endsWith(".json")) {
            loaded.add(name.slice(0, -".json".length));
          }
        }

        listed = loaded;
        return loaded;
      })
      .catch((error: unknown) => {
        listing = null;

        if (isMissing(error)) {
          listed = new Set<string>();
          return listed;
        }

        return null;
      });

    return listing;
  };

  return {
    async hasRecord(id: string): Promise<boolean | null> {
      // Our own writes answer first, and without waiting for the listing:
      // they are the one source that cannot be stale.
      if (written.has(id)) {
        return true;
      }

      const loaded = listed ?? (await loadListing());

      // Re-check: the listing may have been awaited across a write.
      if (written.has(id)) {
        return true;
      }

      return loaded?.has(id) ?? null;
    },

    async read(id: string): Promise<string | null> {
      // Any failure reads as "no record": for this cache a misclassified
      // transient error costs one refetch by the download engine's backfill,
      // which is the documented degradation path — not worth the
      // list-the-directory dance capacitorSnapshotIO needs for data it
      // cannot refetch.
      try {
        const result = await (
          await resolveFs()
        ).readFile({
          path: pathFor(id),
          directory: DATA_DIRECTORY,
          encoding: UTF8_ENCODING,
        });
        return typeof result.data === "string"
          ? result.data
          : await result.data.text();
      } catch {
        return null;
      }
    },

    async write(id: string, data: string): Promise<void> {
      await (
        await resolveFs()
      ).writeFile({
        path: pathFor(id),
        directory: DATA_DIRECTORY,
        data,
        encoding: UTF8_ENCODING,
        recursive: true,
      });
      // Only after the write resolves: claiming a record the disk does not
      // have would let a one-field update skip the read that preserves its
      // sibling, and write null over the sibling's only copy.
      written.add(id);
    },

    async remove(id: string): Promise<void> {
      try {
        await (
          await resolveFs()
        ).deleteFile({
          path: pathFor(id),
          directory: DATA_DIRECTORY,
        });
      } catch (error) {
        // Capacitor reports a missing path as a plain Error with a
        // platform-varying message (see capacitorSnapshotIO's note). A
        // missing record is a successful remove; a wrong guess here leaves
        // an orphaned file at worst.
        if (isMissing(error)) {
          written.delete(id);
          listed?.delete(id);
          return;
        }

        // A delete that genuinely failed leaves the file there, so the id
        // STAYS listed. The two ways to be wrong are not symmetric: claiming
        // a record exists when it doesn't costs one read that returns null,
        // while claiming it's absent when it isn't lets a one-field update
        // skip the read that preserves its sibling — writing null over the
        // sibling's only durable copy. Always err toward "present".
        throw error;
      }

      written.delete(id);
      listed?.delete(id);
    },
  };
}

// The pre-migration localStorage location, still used as the store's default
// on non-native platforms (where the quota ceiling is not in play and no
// Capacitor filesystem exists). MUST stay in sync with the store's
// `storageKey` ("mobileVideos.v3") — the store reads this same location as
// the LEGACY source when migrating a native device's records to files.
export function legacyMediaCacheKey(id: string): string {
  return `mobileVideos.v3.media.${id}`;
}

/** Non-native implementation: the original localStorage keys behind the
 * async interface, so the store has one code path everywhere. */
export function createLocalStorageMediaCacheIO(): MediaCacheIO {
  return {
    storesInLegacyLocation: true,
    // Exact and free here — localStorage has no failing-probe problem to
    // avoid, so this is just the read without the parse.
    async hasRecord(id: string): Promise<boolean | null> {
      return localStorage.getItem(legacyMediaCacheKey(id)) != null;
    },
    async read(id: string): Promise<string | null> {
      return localStorage.getItem(legacyMediaCacheKey(id));
    },
    async write(id: string, data: string): Promise<void> {
      localStorage.setItem(legacyMediaCacheKey(id), data);
    },
    async remove(id: string): Promise<void> {
      localStorage.removeItem(legacyMediaCacheKey(id));
    },
  };
}

export function defaultMediaCacheIO(): MediaCacheIO {
  return Capacitor.isNativePlatform()
    ? createCapacitorMediaCacheIO()
    : createLocalStorageMediaCacheIO();
}
